import clsx from 'clsx'

/**
 * The strip above the board: four tiles, each a count of something the
 * platform holds, each with a way into the list behind it.
 *
 * Nothing here is automated and nothing says it is. The platform sends no
 * email on its own and drafts none, so the lead tile counts relationships that
 * have gone quiet under the board's rules and opens that list; it does not
 * promise a check-in is waiting. Enrichment runs only when a person asks for a
 * lookup, and the tile counts contacts that carry one.
 *
 * Founder's decision, 4 Nov 2026: duplicates are merged on their own and
 * nothing waits for a confirmation, so there is no duplicate queue to count.
 * The clean-up tile says so and opens the merge log; the last tile counts only
 * the entries the engine is unsure of.
 */
export interface TodayStripProps {
  /** Contacts in the Cooling stage; null while the board is being read. */
  goingQuiet: number | null
  /** The Cooling rule, verbatim from the server's definitions; shown on hover. */
  coolingRule?: string
  relationshipsFailed?: boolean
  onOpenGoingQuiet: () => void

  /** Opens the merge log, where every automatic merge can be undone. */
  onOpenMergeLog: () => void

  /** Contacts carrying an external lookup, out of every live contact. */
  enrichment: { enriched: number; total: number } | null

  /** Entries the engine is unsure of; null while the board is being read. */
  unsure: number | null
  onOpenUnsure: () => void

  /** True while a panel shares the row, so the tiles go two by two sooner. */
  narrow?: boolean
}

const TILE = 'flex min-w-0 flex-col gap-1.5 rounded-lg border p-3'
const KICKER = 'font-mono text-[9.5px] uppercase tracking-[0.16em]'
const SUB = 'line-clamp-2 text-[11px] leading-snug text-stone-500'
const SECONDARY =
  'self-start rounded-md border border-amber-400/15 bg-black/30 px-2.5 py-1 text-[11px] font-medium text-stone-200 hover:border-amber-400/35 hover:text-amber-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60'

export default function TodayStrip(props: TodayStripProps) {
  const { goingQuiet, relationshipsFailed, enrichment, unsure } = props
  const percent = enrichment && enrichment.total > 0 ? Math.round((enrichment.enriched / enrichment.total) * 100) : 0

  return (
    <section
      id="crm-today"
      aria-label="Today"
      className={clsx(
        'grid grid-cols-2 gap-3',
        props.narrow ? 'min-[1700px]:grid-cols-4' : 'min-[1280px]:grid-cols-4',
      )}
    >
      <div className={clsx(TILE, 'border-amber-400/30 bg-amber-400/[0.04]')}>
        <div className={clsx(KICKER, 'text-amber-300')}>Needs you today</div>
        {relationshipsFailed ? (
          <div role="alert" className="text-[12px] text-red-300">
            The relationship board could not be read, so quiet relationships cannot be counted.
          </div>
        ) : (
          <>
            <div className="font-display text-[23px] font-semibold leading-[1.1] text-stone-50">
              {goingQuiet === null
                ? 'Counting relationships…'
                : goingQuiet === 0
                  ? 'No relationships are going quiet'
                  : `${goingQuiet} ${goingQuiet === 1 ? 'relationship is' : 'relationships are'} going quiet`}
            </div>
            <div className={clsx(SUB, 'text-stone-400')} title={props.coolingRule}>
              {goingQuiet === 0
                ? 'Nobody on your desk has passed the quiet limits.'
                : 'Each was on deals in the last 12 months and has gone quiet by the board’s rules.'}
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-x-2.5 gap-y-1 pt-1">
              <button
                type="button"
                onClick={props.onOpenGoingQuiet}
                disabled={!goingQuiet}
                className="rounded-md border border-amber-400/45 bg-amber-400/[0.14] px-3 py-1 text-[11px] font-semibold text-amber-100 hover:bg-amber-400/[0.22] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Open the list
              </button>
              <span className="text-[10.5px] text-stone-500">Nothing is sent from here.</span>
            </div>
          </>
        )}
      </div>

      <div className={clsx(TILE, 'border-white/[0.08] bg-black/30')}>
        <div className={clsx(KICKER, 'text-stone-500')}>Clean-up</div>
        <div className="text-[14px] font-semibold text-stone-100">Duplicates merge on their own</div>
        <div className={SUB}>The dedup pass runs after every capture. Every merge is on the log and can be undone.</div>
        <button type="button" onClick={props.onOpenMergeLog} className={clsx(SECONDARY, 'mt-auto')}>
          Open the merge log
        </button>
      </div>

      <div className={clsx(TILE, 'border-white/[0.08] bg-black/30')}>
        <div className={clsx(KICKER, 'text-stone-500')}>Enrichment</div>
        {enrichment === null ? (
          <div className="text-[13px] text-stone-400">Counting…</div>
        ) : (
          <>
            <div className="text-[14px] font-semibold text-stone-100">
              {enrichment.enriched} of {enrichment.total} contacts enriched
            </div>
            <div
              className="h-1.5 overflow-hidden rounded bg-stone-800"
              role="progressbar"
              aria-label="Contacts enriched"
              aria-valuemin={0}
              aria-valuemax={enrichment.total}
              aria-valuenow={enrichment.enriched}
              title={`${percent}%`}
            >
              <div className="h-full bg-amber-400" style={{ width: `${percent}%` }} />
            </div>
            <div className={SUB}>A lookup runs only when someone asks. Every fact keeps its source.</div>
          </>
        )}
      </div>

      <div className={clsx(TILE, 'border-white/[0.08] bg-black/30')}>
        <div className={clsx(KICKER, 'text-stone-500')}>Unsure entries</div>
        {relationshipsFailed ? (
          <div className="text-[12px] text-stone-400">The count could not be read.</div>
        ) : unsure === null ? (
          <div className="text-[13px] text-stone-400">Counting…</div>
        ) : (
          <>
            <div className="text-[14px] font-semibold text-stone-100">
              {unsure === 0 ? 'No unsure entries' : `${unsure} unsure ${unsure === 1 ? 'entry' : 'entries'}`}
            </div>
            <div className={SUB}>Everything is added automatically. Flagged only where the engine doubts the entry, with why.</div>
            {unsure > 0 ? (
              <button type="button" onClick={props.onOpenUnsure} className={clsx(SECONDARY, 'mt-auto')}>
                Open the list
              </button>
            ) : null}
          </>
        )}
      </div>
    </section>
  )
}
